import Bugsnag from '@bugsnag/js';

type StorageKind = 'localStorage' | 'sessionStorage';
type ReportKind = StorageKind | 'cookies';

const unavailabilityReported: Record<ReportKind, boolean> = {
  localStorage: false,
  sessionStorage: false,
  cookies: false,
};

const reportUnavailable = (kind: ReportKind, operation: string, error: unknown) => {
  if (unavailabilityReported[kind]) return;

  unavailabilityReported[kind] = true;

  const reason = error instanceof Error ? `${error.name}: ${error.message}` : String(error);

  Bugsnag.leaveBreadcrumb(`${kind} is unavailable`, { operation, reason });

  Bugsnag.notify(new Error(`${kind} is unavailable`), (event) => {
    event.severity = 'info';
    event.addMetadata('storage', { kind, operation, reason });
  });
};

const getStorage = (kind: StorageKind, operation: string): Storage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window[kind];
  } catch (error) {
    reportUnavailable(kind, operation, error);
    return null;
  }
};

const createSafeStorage = (kind: StorageKind) => ({
  isAvailable: (): boolean => {
    const storage = getStorage(kind, 'isAvailable');
    if (!storage) return false;

    try {
      const probeKey = `__storage_probe_${kind}__`;
      storage.setItem(probeKey, '1');
      storage.removeItem(probeKey);

      return true;
    } catch (error) {
      reportUnavailable(kind, 'isAvailable', error);

      return false;
    }
  },

  getItem: (key: string): string | null => {
    const storage = getStorage(kind, 'getItem');
    if (!storage) return null;

    try {
      return storage.getItem(key);
    } catch (error) {
      reportUnavailable(kind, 'getItem', error);
      return null;
    }
  },

  setItem: (key: string, value: string): void => {
    const storage = getStorage(kind, 'setItem');
    if (!storage) return;

    try {
      storage.setItem(key, value);
    } catch (error) {
      reportUnavailable(kind, 'setItem', error);
    }
  },

  removeItem: (key: string): void => {
    const storage = getStorage(kind, 'removeItem');
    if (!storage) return;

    try {
      storage.removeItem(key);
    } catch (error) {
      reportUnavailable(kind, 'removeItem', error);
    }
  },

  keys: (): string[] => {
    const storage = getStorage(kind, 'keys');
    if (!storage) return [];

    try {
      return Object.keys(storage);
    } catch (error) {
      reportUnavailable(kind, 'keys', error);
      return [];
    }
  },
});

export const safeLocalStorage = createSafeStorage('localStorage');
export const safeSessionStorage = createSafeStorage('sessionStorage');

const COOKIE_PROBE_KEY = '__storage_probe_cookies__';

export const cookiesAreAvailable = (): boolean => {
  if (typeof document === 'undefined') return false;

  try {
    document.cookie = `${COOKIE_PROBE_KEY}=1; SameSite=Lax; path=/`;

    const written = document.cookie.includes(`${COOKIE_PROBE_KEY}=1`);

    document.cookie = `${COOKIE_PROBE_KEY}=; Max-Age=0; SameSite=Lax; path=/`;

    if (!written) reportUnavailable('cookies', 'probe', new Error('Probe cookie was not persisted'));

    return written;
  } catch (error) {
    reportUnavailable('cookies', 'probe', error);

    return false;
  }
};

export const requiredStorageIsAvailable = (): boolean =>
  safeLocalStorage.isAvailable() && safeSessionStorage.isAvailable() && cookiesAreAvailable();
